import { SecurityType } from '@biasafe.models';
import type {
  Industry,
  Sector,
  SecurityType as SecurityTypeValue,
  SubIndustry,
  SuperSector,
} from '@biasafe.models';

/**
 * Static eligibility catalogs — the Morningstar Global Equity Classification
 * Structure (GECS) sectors / industry groups / industries, security types, and
 * the country universe.
 *
 * The GICS hierarchy (SECTORS_RAW / INDUSTRIES_RAW / SUB_INDUSTRIES_RAW) is
 * GENERATED from the canonical backend vintage — the Sector / IndustryGroup /
 * Industry Literals in quant_toolbox/universe/universe_type.py — so every code
 * the user can exclude is a valid member of categorical_exclusions.{sector_id,
 * industry_group_id, industry_id} and the EligibleUniverseConfig validator
 * (extra="forbid" + Literal) always accepts it. Regenerate when those Literals
 * change. Security types and the country / exchange universe are still
 * transcribed from the Figma export
 * `designer/src/app/components/universe-data.ts` (securityTypes / allCountries /
 * majorExchanges).
 *
 * These are STATIC build-time choices, NOT live data: the user picks exclusions
 * from them to assemble the eligible-universe config JSON. The ClickHouse-backed
 * classification endpoints (`/securities/classification/*`,
 * `/securities/universes/security-items`) only ADD per-item security counts and
 * scoping to the selected universe — a preview luxury. When those endpoints are
 * unavailable (e.g. a ClickHouse instance with no Morningstar data) the wizard
 * falls back to these catalogs so the config can still be built and submitted.
 * Same principle as STATIC_UNIVERSES in `staticUniverses.ts`.
 *
 * Shape is adapted to the FE orval models. GECS codes are hierarchical (sector =
 * 3 digits, industry = 5, sub-industry = 8), so the parent codes are derived
 * from the code prefix and superSector follows the leading digit (1 = cyclical,
 * 2 = defensive, 3 = sensitive).
 */

// Generated from lib/py/biasafe/.../static_data/classification.py, the single
// source of truth. Do not hand-edit: test_classification_consistency.py fails
// the build if this drifts from it.
//
// The COMPREHENSIVE POINT-IN-TIME UNION -- every code index_constituents has
// ever carried plus every GECS 2026 code. `CLASSIFICATION_STATUS` says which of
// the three vintages each belongs to; the picker renders only `current`, while
// a saved config still resolves a retired code to a readable name.
const SECTORS_RAW: ReadonlyArray<{ code: string; label: string }> = [
  { code: '101', label: 'Basic Materials' },
  { code: '102', label: 'Consumer Cyclical' },
  { code: '103', label: 'Financial Services' },
  { code: '104', label: 'Real Estate' },
  { code: '205', label: 'Consumer Defensive' },
  { code: '206', label: 'Healthcare' },
  { code: '207', label: 'Utilities' },
  { code: '308', label: 'Communication Services' },
  { code: '309', label: 'Energy' },
  { code: '310', label: 'Industrials' },
  { code: '311', label: 'Technology' },
];

const INDUSTRIES_RAW: ReadonlyArray<{ code: string; label: string }> = [
  // 127 industry groups
  { code: '10101', label: 'Agriculture' },  // retired (2011)
  { code: '10102', label: 'Building Materials' },  // retired (2011)
  { code: '10103', label: 'Chemicals' },  // retired (2011)
  { code: '10104', label: 'Coal' },  // retired (2011)
  { code: '10105', label: 'Forest Products' },  // retired (2011)
  { code: '10106', label: 'Metals & Mining' },  // retired (2011)
  { code: '10107', label: 'Steel' },  // retired (2011)
  { code: '10110', label: 'Agriculture' },  // current (2026)
  { code: '10120', label: 'Building Materials' },  // current (2026)
  { code: '10130', label: 'Chemicals' },  // current (2026)
  { code: '10140', label: 'Forest Products & Services' },  // current (2026)
  { code: '10150', label: 'Metals, Minerals & Mining' },  // current (2026)
  { code: '10160', label: 'Iron & Steel' },  // current (2026)
  { code: '10200', label: 'Vehicles & Parts' },  // current (2026)
  { code: '10208', label: 'Advertising' },  // retired (2011)
  { code: '10209', label: 'Autos' },  // retired (2011)
  { code: '10210', label: 'Entertainment' },  // retired (2011)
  { code: '10211', label: 'Homebuilding & Construction' },  // retired (2011)
  { code: '10212', label: 'Manufacturing–Apparel & Furniture' },  // retired (2011)
  { code: '10213', label: 'Packaging & Containers' },  // retired (2011)
  { code: '10214', label: 'Personal Services' },  // retired (2011)
  { code: '10215', label: 'Publishing' },  // retired (2011)
  { code: '10216', label: 'Restaurants' },  // retired (2011)
  { code: '10217', label: 'Retail–Apparel & Specialty' },  // retired (2011)
  { code: '10218', label: 'Travel & Leisure' },  // retired (2011)
  { code: '10220', label: 'Furnishings, Fixtures & Appliances' },  // current (2026)
  { code: '10230', label: 'Homebuilding & Construction' },  // current (2026)
  { code: '10240', label: 'Manufacturing - Apparel & Accessories' },  // current (2026)
  { code: '10250', label: 'Packaging & Containers' },  // current (2026)
  { code: '10260', label: 'Consumer Services' },  // current (2026)
  { code: '10270', label: 'Restaurants' },  // current (2026)
  { code: '10280', label: 'Retail - Cyclical' },  // current (2026)
  { code: '10290', label: 'Travel & Leisure' },  // current (2026)
  { code: '10310', label: 'Asset Management' },  // current (2026)
  { code: '10319', label: 'Asset Management' },  // retired (2011)
  { code: '10320', label: 'Banks' },  // current (2026)
  { code: '10321', label: 'Brokers & Exchanges' },  // retired (2011)
  { code: '10322', label: 'Credit Services' },  // retired (2011)
  { code: '10323', label: 'Insurance' },  // retired (2011)
  { code: '10324', label: 'Insurance–Life' },  // retired (2011)
  { code: '10325', label: 'Insurance–Property' },  // retired (2011)
  { code: '10326', label: 'Insurance–Specialty' },  // retired (2011)
  { code: '10330', label: 'Capital Markets' },  // current (2026)
  { code: '10340', label: 'Insurance' },  // current (2026)
  { code: '10350', label: 'Diversified Financial Services' },  // current (2026)
  { code: '10360', label: 'Credit Services' },  // current (2026)
  { code: '10370', label: 'REIT - Mortgage' },  // pending2026 (2026)
  { code: '10380', label: 'Fintech' },  // pending2026 (2026)
  { code: '10410', label: 'Real Estate' },  // current (2026)
  { code: '10420', label: 'REITs' },  // current (2026)
  { code: '10427', label: 'Real Estate' },  // retired (2011)
  { code: '10428', label: 'REITs' },  // retired (2011)
  { code: '20510', label: 'Beverages' },  // current (2026)
  { code: '20520', label: 'Beverages—Non-Alcoholic' },  // current (2019)
  { code: '20525', label: 'Consumer Packaged Goods' },  // current (2026)
  { code: '20529', label: 'Beverages–Alcoholic' },  // retired (2011)
  { code: '20530', label: 'Beverages–Non-Alcoholic' },  // retired (2011)
  { code: '20531', label: 'Consumer' },  // retired (2011)
  { code: '20532', label: 'Education' },  // retired (2011)
  { code: '20533', label: 'Retail–Defensive' },  // retired (2011)
  { code: '20534', label: 'Tobacco Products' },  // retired (2011)
  { code: '20540', label: 'Education' },  // current (2026)
  { code: '20550', label: 'Retail - Defensive' },  // current (2026)
  { code: '20560', label: 'Tobacco Products' },  // current (2026)
  { code: '20570', label: 'Cannabis Products' },  // pending2026 (2026)
  { code: '20610', label: 'Biopharmaceutical' },  // current (2026)
  { code: '20620', label: 'Drug Manufacturers' },  // current (2026)
  { code: '20630', label: 'Healthcare Plans' },  // current (2026)
  { code: '20635', label: 'Biotechnology' },  // retired (2011)
  { code: '20636', label: 'Drug' },  // retired (2011)
  { code: '20637', label: 'Healthcare Plans' },  // retired (2011)
  { code: '20638', label: 'Healthcare' },  // retired (2011)
  { code: '20639', label: 'Medical Devices' },  // retired (2011)
  { code: '20640', label: 'Medical' },  // retired (2011)
  { code: '20641', label: 'Medical' },  // retired (2011)
  { code: '20642', label: 'Medical' },  // retired (2011)
  { code: '20645', label: 'Healthcare Providers & Services' },  // current (2026)
  { code: '20650', label: 'Medical Devices & Instruments' },  // current (2026)
  { code: '20660', label: 'Medical Diagnostic & Research Services' },  // current (2026)
  { code: '20670', label: 'Medical Distribution' },  // current (2026)
  { code: '20710', label: 'Utilities - Independent Power Producers' },  // current (2026)
  { code: '20720', label: 'Utilities - Regulated' },  // current (2026)
  { code: '20730', label: 'Utilities - Diversified' },  // pending2026 (2026)
  { code: '20743', label: 'Utilities–Independent Power Producers' },  // retired (2011)
  { code: '20744', label: 'Utilities–Regulated' },  // retired (2011)
  { code: '30810', label: 'Telecommunication Services' },  // current (2026)
  { code: '30820', label: 'Media - Diversified' },  // current (2026)
  { code: '30830', label: 'Interactive Media' },  // current (2026)
  { code: '30845', label: 'Communication Services' },  // retired (2011)
  { code: '30910', label: 'Oil & Gas' },  // current (2026)
  { code: '30920', label: 'Other Energy Sources' },  // current (2026)
  { code: '30946', label: 'Oil & Gas–Drilling' },  // retired (2011)
  { code: '30947', label: 'Oil & Gas–E&P' },  // retired (2011)
  { code: '30948', label: 'Oil & Gas–Integrated' },  // retired (2011)
  { code: '30949', label: 'Oil & Gas–Midstream' },  // retired (2011)
  { code: '30950', label: 'Oil & Gas–Refining' },  // retired (2011)
  { code: '30951', label: 'Oil & Gas–Services' },  // retired (2011)
  { code: '31010', label: 'Aerospace & Defense' },  // current (2026)
  { code: '31020', label: 'Business Services' },  // current (2026)
  { code: '31030', label: 'Conglomerates' },  // current (2026)
  { code: '31040', label: 'Construction' },  // current (2026)
  { code: '31045', label: 'Building Products & Equipment' },  // pending2026 (2026)
  { code: '31050', label: 'Heavy-Duty Machinery & Transport Equipment' },  // current (2026)
  { code: '31052', label: 'Aerospace & Defense' },  // retired (2011)
  { code: '31053', label: 'Airlines' },  // retired (2011)
  { code: '31054', label: 'Business Services' },  // retired (2011)
  { code: '31055', label: 'Conglomerates' },  // retired (2011)
  { code: '31056', label: 'Consulting & Outsourcing' },  // retired (2011)
  { code: '31057', label: 'Employment' },  // retired (2011)
  { code: '31058', label: 'Engineering & Construction' },  // retired (2011)
  { code: '31059', label: 'Farm & Construction' },  // retired (2011)
  { code: '31060', label: 'Industrial Distribution & Dealership' },  // current (2026)
  { code: '31061', label: 'Industrial Products' },  // retired (2011)
  { code: '31062', label: 'Transportation & Logistics' },  // retired (2011)
  { code: '31063', label: 'Truck' },  // retired (2011)
  { code: '31064', label: 'Waste' },  // retired (2011)
  { code: '31070', label: 'Industrial Products' },  // current (2026)
  { code: '31080', label: 'Transportation' },  // current (2026)
  { code: '31090', label: 'Waste Management' },  // current (2026)
  { code: '31110', label: 'Software' },  // current (2026)
  { code: '31120', label: 'Hardware' },  // current (2026)
  { code: '31130', label: 'Semiconductors' },  // current (2026)
  { code: '31165', label: 'Application' },  // retired (2011)
  { code: '31166', label: 'Communication' },  // retired (2011)
  { code: '31167', label: 'Computer' },  // retired (2011)
  { code: '31168', label: 'Online Media' },  // retired (2011)
  { code: '31169', label: 'Semiconductors' },  // retired (2011)
];

const SUB_INDUSTRIES_RAW: ReadonlyArray<{ code: string; label: string }> = [
  // 316 industries
  { code: '10101001', label: 'Agricultural Inputs' },  // retired (2011)
  { code: '10102002', label: 'Building Materials' },  // retired (2011)
  { code: '10103003', label: 'Chemicals' },  // retired (2011)
  { code: '10103004', label: 'Specialty Chemicals' },  // retired (2011)
  { code: '10104005', label: 'Coal' },  // retired (2011)
  { code: '10105006', label: 'Lumber & Wood Production' },  // retired (2011)
  { code: '10105007', label: 'Paper & Paper Products' },  // retired (2011)
  { code: '10106008', label: 'Aluminum' },  // retired (2011)
  { code: '10106009', label: 'Copper' },  // retired (2011)
  { code: '10106010', label: 'Gold' },  // retired (2011)
  { code: '10106011', label: 'Industrial Metals & Minerals' },  // retired (2011)
  { code: '10106012', label: 'Silver' },  // retired (2011)
  { code: '10107013', label: 'Steel' },  // retired (2011)
  { code: '10110010', label: 'Agriculture Inputs' },  // current (2026)
  { code: '10120010', label: 'Building Materials' },  // current (2026)
  { code: '10130010', label: 'Basic Chemicals' },  // current (2026)
  { code: '10130020', label: 'Specialty Chemicals' },  // current (2026)
  { code: '10130030', label: 'Diversified Chemicals' },  // pending2026 (2026)
  { code: '10130040', label: 'Industrial Gases' },  // pending2026 (2026)
  { code: '10140010', label: 'Forest Products & Services' },  // current (2026)
  { code: '10140020', label: 'Paper & Pulp' },  // current (2026)
  { code: '10150010', label: 'Aluminum' },  // current (2026)
  { code: '10150020', label: 'Copper' },  // current (2026)
  { code: '10150030', label: 'Industrial Metals, Minerals & Mining' },  // current (2026)
  { code: '10150040', label: 'Gold' },  // current (2026)
  { code: '10150050', label: 'Silver' },  // current (2026)
  { code: '10150060', label: 'Precious Metals, Minerals & Mining' },  // current (2026)
  { code: '10150070', label: 'Diamonds & Gemstones' },  // pending2026 (2026)
  { code: '10160010', label: 'Coking Coal' },  // current (2026)
  { code: '10160020', label: 'Iron & Steel' },  // current (2026)
  { code: '10200010', label: 'Auto & Truck Dealerships' },  // current (2026)
  { code: '10200020', label: 'Auto Manufacturers' },  // current (2026)
  { code: '10200030', label: 'Auto Parts' },  // current (2026)
  { code: '10200040', label: 'Recreational Vehicles' },  // current (2026)
  { code: '10200050', label: 'Tire Manufacturers' },  // pending2026 (2026)
  { code: '10208014', label: 'Advertising Agencies' },  // retired (2011)
  { code: '10208015', label: 'Marketing Services' },  // retired (2011)
  { code: '10209016', label: 'Auto & Truck Dealerships' },  // retired (2011)
  { code: '10209017', label: 'Auto Manufacturers' },  // retired (2011)
  { code: '10209018', label: 'Auto Parts' },  // retired (2011)
  { code: '10209019', label: 'Recreational Vehicles' },  // retired (2011)
  { code: '10209020', label: 'Rubber & Plastics' },  // retired (2011)
  { code: '10210021', label: 'Broadcasting–Radio' },  // retired (2011)
  { code: '10210022', label: 'Broadcasting–TV' },  // retired (2011)
  { code: '10210023', label: 'Media–Diversified' },  // retired (2011)
  { code: '10211024', label: 'Residential Construction' },  // retired (2011)
  { code: '10211025', label: 'Textile Manufacturing' },  // retired (2011)
  { code: '10212026', label: 'Apparel Manufacturing' },  // retired (2011)
  { code: '10212027', label: 'Footwear & Accessories' },  // retired (2011)
  { code: '10212028', label: 'Home Furnishings & Fixtures' },  // retired (2011)
  { code: '10213029', label: 'Packaging & Containers' },  // retired (2011)
  { code: '10214030', label: 'Personal Services' },  // retired (2011)
  { code: '10215031', label: 'Publishing' },  // retired (2011)
  { code: '10216032', label: 'Restaurants' },  // retired (2011)
  { code: '10217033', label: 'Apparel Stores' },  // retired (2011)
  { code: '10217034', label: 'Department Stores' },  // retired (2011)
  { code: '10217035', label: 'Home Improvement Stores' },  // retired (2011)
  { code: '10217036', label: 'Luxury Goods' },  // retired (2011)
  { code: '10217037', label: 'Specialty Retail' },  // retired (2011)
  { code: '10218038', label: 'Gambling' },  // retired (2011)
  { code: '10218039', label: 'Leisure' },  // retired (2011)
  { code: '10218040', label: 'Lodging' },  // retired (2011)
  { code: '10218041', label: 'Resorts & Casinos' },  // retired (2011)
  { code: '10220010', label: 'Furnishings, Fixtures & Appliances' },  // current (2026)
  { code: '10230010', label: 'Residential Construction' },  // current (2026)
  { code: '10240010', label: 'Textile Manufacturing' },  // current (2026)
  { code: '10240020', label: 'Apparel Manufacturing' },  // current (2026)
  { code: '10240030', label: 'Footwear & Accessories' },  // current (2026)
  { code: '10240040', label: 'Luxury Goods' },  // pending2026 (2026)
  { code: '10250010', label: 'Packaging & Containers' },  // current (2026)
  { code: '10260010', label: 'Consumer Services' },  // current (2026)
  { code: '10270010', label: 'Restaurants' },  // current (2026)
  { code: '10280010', label: 'Apparel Retail' },  // current (2026)
  { code: '10280020', label: 'Department Stores' },  // current (2026)
  { code: '10280030', label: 'Home Improvement Retail' },  // current (2026)
  { code: '10280040', label: 'Luxury Goods' },  // current (2019)
  { code: '10280050', label: 'Internet Retail' },  // current (2026)
  { code: '10280060', label: 'Specialty Retail' },  // current (2026)
  { code: '10290010', label: 'Gambling' },  // current (2019)
  { code: '10290020', label: 'Recreational Products' },  // current (2026)
  { code: '10290030', label: 'Lodging' },  // current (2026)
  { code: '10290040', label: 'Resorts & Gaming' },  // current (2026)
  { code: '10290050', label: 'Travel Services' },  // current (2026)
  { code: '10290060', label: 'Recreational Facilities' },  // pending2026 (2026)
  { code: '10310010', label: 'Asset Management' },  // current (2026)
  { code: '10319042', label: 'Asset Management' },  // retired (2011)
  { code: '10320010', label: 'Banks - Diversified' },  // current (2026)
  { code: '10320020', label: 'Banks - Regional' },  // current (2026)
  { code: '10320030', label: 'Mortgage Finance' },  // current (2026)
  { code: '10320043', label: 'Banks–Global' },  // retired (2011)
  { code: '10320044', label: 'Banks–Regional–Africa' },  // retired (2011)
  { code: '10320045', label: 'Banks–Regional–Asia' },  // current (2011)
  { code: '10320046', label: 'Banks–Regional–Australia' },  // retired (2011)
  { code: '10320047', label: 'Banks–Regional–Canada' },  // retired (2011)
  { code: '10320048', label: 'Banks–Regional–Europe' },  // retired (2011)
  { code: '10320049', label: 'Banks–Regional–Latin America' },  // retired (2011)
  { code: '10320050', label: 'Banks–Regional–US' },  // retired (2011)
  { code: '10320051', label: 'Savings & Cooperative Banks' },  // retired (2011)
  { code: '10320052', label: 'Specialty Finance' },  // retired (2011)
  { code: '10321053', label: 'Capital Markets' },  // retired (2011)
  { code: '10321054', label: 'Financial Exchanges' },  // retired (2011)
  { code: '10321055', label: 'Insurance Brokers' },  // retired (2011)
  { code: '10322056', label: 'Credit Services' },  // retired (2011)
  { code: '10323057', label: 'Insurance–Diversified' },  // retired (2011)
  { code: '10324058', label: 'Insurance–Life' },  // retired (2011)
  { code: '10325059', label: 'Insurance–Property & Casualty' },  // retired (2011)
  { code: '10326060', label: 'Insurance–Reinsurance' },  // retired (2011)
  { code: '10326061', label: 'Insurance–Specialty' },  // retired (2011)
  { code: '10330010', label: 'Capital Markets' },  // current (2026)
  { code: '10330020', label: 'Financial Data & Exchanges' },  // current (2026)
  { code: '10340010', label: 'Insurance - Life' },  // current (2026)
  { code: '10340020', label: 'Insurance - Property & Casualty' },  // current (2026)
  { code: '10340030', label: 'Insurance - Reinsurance' },  // current (2026)
  { code: '10340040', label: 'Insurance—Specialty' },  // current (2019)
  { code: '10340050', label: 'Insurance - Brokers' },  // current (2026)
  { code: '10340060', label: 'Insurance - Diversified' },  // current (2026)
  { code: '10350010', label: 'Shell Companies' },  // current (2026)
  { code: '10350020', label: 'Financial Conglomerates' },  // current (2026)
  { code: '10350030', label: 'Conduit Financing' },  // pending2026 (2026)
  { code: '10360010', label: 'Credit & Monetary Solutions' },  // current (2026)
  { code: '10370010', label: 'REIT - Mortgage' },  // pending2026 (2026)
  { code: '10380010', label: 'Payment & Transaction Services' },  // pending2026 (2026)
  { code: '10410010', label: 'Real Estate - Development' },  // current (2026)
  { code: '10410020', label: 'Real Estate Services' },  // current (2026)
  { code: '10410030', label: 'Real Estate - Diversified' },  // current (2026)
  { code: '10410040', label: 'Real Estate - Ownership & Management' },  // pending2026 (2026)
  { code: '10420010', label: 'REIT - Healthcare Facilities' },  // current (2026)
  { code: '10420020', label: 'REIT - Hotel & Motel' },  // current (2026)
  { code: '10420030', label: 'REIT - Industrial' },  // current (2026)
  { code: '10420040', label: 'REIT - Office' },  // current (2026)
  { code: '10420050', label: 'REIT - Residential' },  // current (2026)
  { code: '10420060', label: 'REIT - Retail' },  // current (2026)
  { code: '10420070', label: 'REIT—Mortgage' },  // current (2019)
  { code: '10420080', label: 'REIT - Specialty' },  // current (2026)
  { code: '10420090', label: 'REIT - Diversified' },  // current (2026)
  { code: '10420100', label: 'REIT - Self Storage' },  // pending2026 (2026)
  { code: '10427062', label: 'Real Estate–General' },  // retired (2011)
  { code: '10427063', label: 'Real Estate Services' },  // retired (2011)
  { code: '10428064', label: 'REIT–Diversified' },  // retired (2011)
  { code: '10428065', label: 'REIT–Healthcare Facilities' },  // retired (2011)
  { code: '10428066', label: 'REIT–Hotel & Motel' },  // retired (2011)
  { code: '10428067', label: 'REIT–Industrial' },  // retired (2011)
  { code: '10428068', label: 'REIT–Office' },  // retired (2011)
  { code: '10428069', label: 'REIT–Residential' },  // retired (2011)
  { code: '10428070', label: 'REIT–Retail' },  // retired (2011)
  { code: '20510010', label: 'Beverages - Brewers' },  // current (2026)
  { code: '20510020', label: 'Beverages - Wineries & Distilleries' },  // current (2026)
  { code: '20510030', label: 'Beverages - Non-Alcoholic' },  // pending2026 (2026)
  { code: '20520010', label: 'Beverages—Non-Alcoholic' },  // current (2019)
  { code: '20525010', label: 'Confectioners' },  // current (2026)
  { code: '20525020', label: 'Farm Products & Services' },  // current (2026)
  { code: '20525030', label: 'Household & Personal Products' },  // current (2026)
  { code: '20525040', label: 'Packaged Foods' },  // current (2026)
  { code: '20529071', label: 'Beverages–Brewers' },  // retired (2011)
  { code: '20529072', label: 'Beverages–Wineries & Distilleries' },  // retired (2011)
  { code: '20530073', label: 'Beverages–Soft Drinks' },  // retired (2011)
  { code: '20531074', label: 'Confectioners' },  // retired (2011)
  { code: '20531075', label: 'Farm Products' },  // retired (2011)
  { code: '20531076', label: 'Household & Personal Products' },  // retired (2011)
  { code: '20531077', label: 'Packaged Foods' },  // retired (2011)
  { code: '20532078', label: 'Education & Training Services' },  // retired (2011)
  { code: '20533079', label: 'Discount Stores' },  // retired (2011)
  { code: '20533080', label: 'Pharmaceutical Retailers' },  // retired (2011)
  { code: '20533081', label: 'Food Distribution' },  // retired (2011)
  { code: '20533082', label: 'Grocery Stores' },  // retired (2011)
  { code: '20534083', label: 'Tobacco' },  // retired (2011)
  { code: '20540010', label: 'Education & Training Services' },  // current (2026)
  { code: '20550010', label: 'Discount Stores' },  // current (2026)
  { code: '20550020', label: 'Food Distribution' },  // current (2026)
  { code: '20550030', label: 'Grocery Stores' },  // current (2026)
  { code: '20550040', label: 'Pharmaceutical Retailers' },  // pending2026 (2026)
  { code: '20560010', label: 'Tobacco' },  // current (2026)
  { code: '20570010', label: 'Cannabis' },  // pending2026 (2026)
  { code: '20610010', label: 'Biopharmaceutical' },  // current (2026)
  { code: '20620010', label: 'Drug Manufacturers' },  // current (2026)
  { code: '20620020', label: 'Drug Manufacturers—Specialty & Generic' },  // current (2019)
  { code: '20630010', label: 'Healthcare Plans' },  // current (2026)
  { code: '20635084', label: 'Biotechnology' },  // retired (2011)
  { code: '20636085', label: 'Drug Manufacturers–Major' },  // retired (2011)
  { code: '20636086', label: 'Drug Manufacturers–Specialty & Generic' },  // retired (2011)
  { code: '20637087', label: 'Healthcare Plans' },  // retired (2011)
  { code: '20638088', label: 'Long-Term Care Facilities' },  // retired (2011)
  { code: '20638089', label: 'Medical Care' },  // retired (2011)
  { code: '20639090', label: 'Medical Devices' },  // retired (2011)
  { code: '20640091', label: 'Diagnostics & Research' },  // retired (2011)
  { code: '20641092', label: 'Medical Distribution' },  // retired (2011)
  { code: '20642093', label: 'Medical Instruments & Supplies' },  // retired (2011)
  { code: '20645010', label: 'Medical Care Facilities & Services' },  // current (2026)
  { code: '20645020', label: 'Pharmaceutical Retailers' },  // current (2019)
  { code: '20645030', label: 'Health Information Services' },  // current (2026)
  { code: '20650010', label: 'Medical Devices' },  // current (2026)
  { code: '20650020', label: 'Medical Instruments & Supplies' },  // current (2026)
  { code: '20650030', label: 'Medical Diagnostic & Testing Equipment' },  // pending2026 (2026)
  { code: '20660010', label: 'Medical Diagnostic & Research Services' },  // current (2026)
  { code: '20670010', label: 'Medical Distribution' },  // current (2026)
  { code: '20710010', label: 'Utilities - Independent Power Producers' },  // current (2026)
  { code: '20710020', label: 'Utilities - Renewable' },  // current (2026)
  { code: '20720010', label: 'Utilities - Regulated Water' },  // current (2026)
  { code: '20720020', label: 'Utilities - Regulated Electric' },  // current (2026)
  { code: '20720030', label: 'Utilities - Regulated Gas' },  // current (2026)
  { code: '20720040', label: 'Utilities—Diversified' },  // current (2019)
  { code: '20730010', label: 'Utilities - Diversified' },  // pending2026 (2026)
  { code: '20743094', label: 'Utilities–Independent Power Producers' },  // retired (2011)
  { code: '20744095', label: 'Utilities–Diversified' },  // retired (2011)
  { code: '20744096', label: 'Utilities–Regulated Electric' },  // retired (2011)
  { code: '20744097', label: 'Utilities–Regulated Gas' },  // retired (2011)
  { code: '20744098', label: 'Utilities–Regulated Water' },  // retired (2011)
  { code: '30810010', label: 'Telecom Services' },  // current (2026)
  { code: '30820010', label: 'Advertising Agencies' },  // current (2026)
  { code: '30820020', label: 'Publishing' },  // current (2026)
  { code: '30820030', label: 'Broadcasting' },  // current (2026)
  { code: '30820040', label: 'Entertainment' },  // current (2026)
  { code: '30830010', label: 'Internet Content & Information' },  // current (2026)
  { code: '30830020', label: 'Electronic Gaming & Multimedia' },  // current (2026)
  { code: '30845099', label: 'Pay TV' },  // retired (2011)
  { code: '30845100', label: 'Telecom Services' },  // retired (2011)
  { code: '30910010', label: 'Oil & Gas Drilling' },  // current (2019)
  { code: '30910020', label: 'Oil & Gas E&P' },  // current (2026)
  { code: '30910030', label: 'Oil & Gas Integrated' },  // current (2026)
  { code: '30910040', label: 'Oil & Gas Midstream' },  // current (2026)
  { code: '30910050', label: 'Oil & Gas Refining & Marketing' },  // current (2026)
  { code: '30910060', label: 'Oil & Gas Equipment & Services' },  // current (2026)
  { code: '30910070', label: 'Oil & Gas Royalties' },  // pending2026 (2026)
  { code: '30920010', label: 'Thermal Coal' },  // current (2026)
  { code: '30920020', label: 'Uranium' },  // current (2026)
  { code: '30920030', label: 'Alternative Fuels' },  // pending2026 (2026)
  { code: '30946101', label: 'Oil & Gas Drilling' },  // retired (2011)
  { code: '30947102', label: 'Oil & Gas E&P' },  // retired (2011)
  { code: '30948103', label: 'Oil & Gas Integrated' },  // retired (2011)
  { code: '30949104', label: 'Oil & Gas Midstream' },  // retired (2011)
  { code: '30950105', label: 'Oil & Gas Refining & Marketing' },  // retired (2011)
  { code: '30951106', label: 'Oil & Gas Equipment & Services' },  // retired (2011)
  { code: '31010010', label: 'Aerospace & Defense' },  // current (2026)
  { code: '31020010', label: 'Specialty Business Services' },  // current (2026)
  { code: '31020020', label: 'Consulting Services' },  // current (2026)
  { code: '31020030', label: 'Rental & Leasing Services' },  // current (2026)
  { code: '31020040', label: 'Security & Protection Services' },  // current (2026)
  { code: '31020050', label: 'Staffing & Employment Services' },  // current (2026)
  { code: '31030010', label: 'Conglomerates' },  // current (2026)
  { code: '31040010', label: 'Engineering & Construction' },  // current (2026)
  { code: '31040020', label: 'Infrastructure Operations' },  // current (2019)
  { code: '31040030', label: 'Building Products & Equipment' },  // current (2019)
  { code: '31045010', label: 'Building Products & Equipment' },  // pending2026 (2026)
  { code: '31050010', label: 'Heavy-Duty Machinery & Transport Equipment' },  // current (2026)
  { code: '31052107', label: 'Aerospace & Defense' },  // retired (2011)
  { code: '31053108', label: 'Airlines' },  // retired (2011)
  { code: '31054109', label: 'Business Services' },  // retired (2011)
  { code: '31055110', label: 'Conglomerates' },  // retired (2011)
  { code: '31056111', label: 'Rental & Leasing Services' },  // retired (2011)
  { code: '31056112', label: 'Security & Protection Services' },  // retired (2011)
  { code: '31057113', label: 'Staffing & Outsourcing Services' },  // retired (2011)
  { code: '31058114', label: 'Engineering & Construction' },  // retired (2011)
  { code: '31058115', label: 'Infrastructure Operations' },  // retired (2011)
  { code: '31059116', label: 'Farm & Construction Machinery' },  // retired (2011)
  { code: '31060010', label: 'Industrial Distribution & Dealership' },  // current (2026)
  { code: '31060117', label: 'Industrial Distribution' },  // retired (2011)
  { code: '31061118', label: 'Business Equipment' },  // retired (2011)
  { code: '31061119', label: 'Diversified Industrials' },  // retired (2011)
  { code: '31061120', label: 'Metal Fabrication' },  // retired (2011)
  { code: '31061121', label: 'Pollution & Treatment Controls' },  // retired (2011)
  { code: '31061122', label: 'Tools & Accessories' },  // retired (2011)
  { code: '31062123', label: 'Airports & Air Services' },  // retired (2011)
  { code: '31062124', label: 'Integrated Shipping & Logistics' },  // retired (2011)
  { code: '31062125', label: 'Railroads' },  // retired (2011)
  { code: '31062126', label: 'Shipping & Ports' },  // retired (2011)
  { code: '31062127', label: 'Trucking' },  // retired (2011)
  { code: '31063128', label: 'Truck Manufacturing' },  // retired (2011)
  { code: '31064129', label: 'Waste Management' },  // retired (2011)
  { code: '31070010', label: 'Business Equipment & Supplies' },  // current (2026)
  { code: '31070020', label: 'Specialty Industrial Machinery' },  // current (2026)
  { code: '31070030', label: 'Metal Fabrication' },  // current (2026)
  { code: '31070040', label: 'Pollution & Treatment Controls' },  // current (2026)
  { code: '31070050', label: 'Tools & Accessories' },  // current (2026)
  { code: '31070060', label: 'Electrical Equipment & Parts' },  // current (2026)
  { code: '31070070', label: 'Alternative Energy Equipment' },  // pending2026 (2026)
  { code: '31080010', label: 'Airports & Air Services' },  // current (2026)
  { code: '31080020', label: 'Airlines' },  // current (2026)
  { code: '31080030', label: 'Railroads' },  // current (2026)
  { code: '31080040', label: 'Marine Shipping' },  // current (2026)
  { code: '31080050', label: 'Trucking' },  // current (2026)
  { code: '31080060', label: 'Integrated Freight & Logistics' },  // current (2026)
  { code: '31080070', label: 'Highways & Rail Infrastructure' },  // pending2026 (2026)
  { code: '31080080', label: 'Port Operator' },  // pending2026 (2026)
  { code: '31090010', label: 'Waste Management' },  // current (2026)
  { code: '31110010', label: 'Information Technology Services' },  // current (2026)
  { code: '31110020', label: 'Software - Application' },  // current (2026)
  { code: '31110030', label: 'Software - Infrastructure' },  // current (2026)
  { code: '31110040', label: 'Crypto Mining' },  // pending2026 (2026)
  { code: '31120010', label: 'Communication Equipment' },  // current (2026)
  { code: '31120020', label: 'Computer Hardware' },  // current (2026)
  { code: '31120030', label: 'Consumer Electronics' },  // current (2026)
  { code: '31120040', label: 'Electronic Components' },  // current (2026)
  { code: '31120050', label: 'Electronics & Computer Distribution' },  // current (2026)
  { code: '31120060', label: 'Technical & Safety Instruments' },  // current (2026)
  { code: '31130010', label: 'Semiconductor Equipment & Materials' },  // current (2026)
  { code: '31130020', label: 'Semiconductors' },  // current (2026)
  { code: '31130030', label: 'Solar' },  // current (2019)
  { code: '31165130', label: 'Electronic Gaming & Multimedia' },  // retired (2011)
  { code: '31165131', label: 'Healthcare Information Services' },  // retired (2011)
  { code: '31165132', label: 'Information Technology Services' },  // retired (2011)
  { code: '31165133', label: 'Software–Application' },  // retired (2011)
  { code: '31165134', label: 'Software–Infrastructure' },  // retired (2011)
  { code: '31166135', label: 'Communication Equipment' },  // retired (2011)
  { code: '31167136', label: 'Computer Distribution' },  // retired (2011)
  { code: '31167137', label: 'Computer Systems' },  // retired (2011)
  { code: '31167138', label: 'Consumer Electronics' },  // retired (2011)
  { code: '31167139', label: 'Contract Manufacturers' },  // retired (2011)
  { code: '31167140', label: 'Data Storage' },  // retired (2011)
  { code: '31167141', label: 'Electronic Components' },  // retired (2011)
  { code: '31167142', label: 'Electronics Distribution' },  // retired (2011)
  { code: '31167143', label: 'Scientific & Technical Instruments' },  // retired (2011)
  { code: '31168144', label: 'Internet Content & Information' },  // retired (2011)
  { code: '31169145', label: 'Semiconductor Equipment & Materials' },  // retired (2011)
  { code: '31169146', label: 'Semiconductor Memory' },  // retired (2011)
  { code: '31169147', label: 'Semiconductors' },  // retired (2011)
  { code: '31169148', label: 'Solar' },  // retired (2011)
];

export const superSectorForCode = (code: string): SuperSector =>
  code.startsWith('1')
    ? 'cyclical'
    : code.startsWith('2')
      ? 'defensive'
      : 'sensitive';

export const STATIC_SECTORS: ReadonlyArray<Sector> = SECTORS_RAW.map((s) => ({
  code: s.code,
  label: s.label,
  superSector: superSectorForCode(s.code),
}));

export const STATIC_INDUSTRIES: ReadonlyArray<Industry> = INDUSTRIES_RAW.map(
  (i) => ({
    code: i.code,
    label: i.label,
    // Industry code's first 3 digits are its parent sector code.
    sectorCode: i.code.slice(0, 3),
  }),
);

export const STATIC_SUB_INDUSTRIES: ReadonlyArray<SubIndustry> =
  SUB_INDUSTRIES_RAW.map((si) => ({
    code: si.code,
    label: si.label,
    // First 5 digits = parent industry, first 3 = parent sector.
    industryCode: si.code.slice(0, 5),
    sectorCode: si.code.slice(0, 3),
  }));

// FE security-type enum values, in declaration order.
/**
 * Types the picker does NOT offer, because the universe cannot contain them.
 *
 * `index_constituents` is a set of EQUITY index constituents: Morningstar's
 * methodology admits operating companies, not fund wrappers. There is also no
 * branch in the backend's security_type CASE that can emit either value, so
 * they are unreachable twice over.
 *
 * Rendering them is worse than useless — a user who ticks "ETF" believes they
 * narrowed the universe, the count does not move, and nothing says why.
 *
 * This hides them from the picker only. The enum stays whole: it is the API
 * contract, and a stored strategy that already carries one of these exclusions
 * must keep round-tripping.
 */
const UNSELECTABLE_SECURITY_TYPES: ReadonlySet<string> = new Set([
  SecurityType.etf,
  SecurityType.closed_end_fund,
]);

export function isSelectableSecurityType(value: string): boolean {
  return !UNSELECTABLE_SECURITY_TYPES.has(value);
}

export const STATIC_SECURITY_TYPES: ReadonlyArray<SecurityTypeValue> =
  Object.values(SecurityType).filter(isSelectableSecurityType);

// Display labels for the security-type enum — the SINGLE source shared by the
// Security Types section and the exclusions preview summary. Acronyms stay
// all-caps (REIT/ETF/ADR), the two equity classes carry their full name, and
// anything unmapped falls back to snake_case → Title Case.
export const SECURITY_TYPE_LABELS: Record<string, string> = {
  common: 'Common Stock',
  preferred: 'Preferred Stock',
  reit: 'REIT',
  etf: 'ETF',
  adr: 'ADR',
  right: 'Right',
  warrants: 'Warrant',
  units: 'Unit',
  tracking_stock: 'Tracking Stock',
  closed_end_fund: 'Closed-End Fund',
};

export const securityTypeLabel = (t: string): string =>
  SECURITY_TYPE_LABELS[t.toLowerCase()] ??
  t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Short codes for the mono badge prefix on exclusion chips — de-pluralised /
// trimmed so the chips stay compact (warrants → warrant, closed_end_fund →
// closed_fund). DISPLAY ONLY: the value sent to the engine is always the full
// SecurityType enum string; this never touches selection identity or removal.
const SECURITY_TYPE_CODES: Record<string, string> = {
  warrants: 'warrant',
  units: 'unit',
  tracking_stock: 'tracking',
  closed_end_fund: 'closed_fund',
};

export const securityTypeCode = (t: string): string =>
  SECURITY_TYPE_CODES[t.toLowerCase()] ?? t;

// Country universe: code + scope bucket + display label. scope maps to the
// geographic_scope option ids (north_america | dev_ex_na | emerging): US/CA are
// North America, other developed markets are dev-ex-NA, the rest are emerging —
// the same split as eligibleUniverseAdapter's SCOPE_TO_REGIONS.
const COUNTRIES_RAW: ReadonlyArray<{
  code: string;
  label: string;
  developed: boolean;
}> = [
  { code: 'US', label: 'United States', developed: true },
  { code: 'CA', label: 'Canada', developed: true },
  { code: 'MX', label: 'Mexico', developed: false },
  { code: 'BR', label: 'Brazil', developed: false },
  { code: 'CL', label: 'Chile', developed: false },
  { code: 'PE', label: 'Peru', developed: false },
  { code: 'CO', label: 'Colombia', developed: false },
  { code: 'KY', label: 'Cayman Islands', developed: false },
  { code: 'CH', label: 'Switzerland', developed: true },
  { code: 'GB', label: 'United Kingdom', developed: true },
  { code: 'DE', label: 'Germany', developed: true },
  { code: 'NL', label: 'Netherlands', developed: true },
  { code: 'FI', label: 'Finland', developed: true },
  { code: 'IT', label: 'Italy', developed: true },
  { code: 'FR', label: 'France', developed: true },
  { code: 'BE', label: 'Belgium', developed: true },
  { code: 'SE', label: 'Sweden', developed: true },
  { code: 'DK', label: 'Denmark', developed: true },
  { code: 'NO', label: 'Norway', developed: true },
  { code: 'IE', label: 'Ireland', developed: true },
  { code: 'ES', label: 'Spain', developed: true },
  { code: 'AT', label: 'Austria', developed: true },
  { code: 'PT', label: 'Portugal', developed: true },
  { code: 'GR', label: 'Greece', developed: true },
  { code: 'PL', label: 'Poland', developed: true },
  { code: 'HU', label: 'Hungary', developed: false },
  { code: 'TR', label: 'Turkey', developed: false },
  { code: 'CZ', label: 'Czech Republic', developed: false },
  { code: 'RU', label: 'Russia', developed: false },
  { code: 'JP', label: 'Japan', developed: true },
  { code: 'AU', label: 'Australia', developed: true },
  { code: 'HK', label: 'Hong Kong', developed: true },
  { code: 'SG', label: 'Singapore', developed: true },
  { code: 'NZ', label: 'New Zealand', developed: true },
  { code: 'KR', label: 'South Korea', developed: false },
  { code: 'ID', label: 'Indonesia', developed: false },
  { code: 'TW', label: 'Taiwan', developed: false },
  { code: 'MY', label: 'Malaysia', developed: false },
  { code: 'TH', label: 'Thailand', developed: false },
  { code: 'PH', label: 'Philippines', developed: false },
  { code: 'IN', label: 'India', developed: false },
  { code: 'CN', label: 'China', developed: false },
  { code: 'PK', label: 'Pakistan', developed: false },
  { code: 'IL', label: 'Israel', developed: true },
  { code: 'ZA', label: 'South Africa', developed: false },
  { code: 'MA', label: 'Morocco', developed: false },
  { code: 'EG', label: 'Egypt', developed: false },
  { code: 'QA', label: 'Qatar', developed: false },
  { code: 'KW', label: 'Kuwait', developed: false },
  { code: 'SA', label: 'Saudi Arabia', developed: false },
  { code: 'AE', label: 'United Arab Emirates', developed: false },
];

export const STATIC_COUNTRIES: ReadonlyArray<{
  code: string;
  scope: string;
  label: string;
}> = COUNTRIES_RAW.map((c) => ({
  code: c.code,
  label: c.label,
  scope:
    c.code === 'US' || c.code === 'CA'
      ? 'north_america'
      : c.developed
        ? 'dev_ex_na'
        : 'emerging',
}));

// Exchange universe — the `code` is the MIC (e.g. XNYS), which is what the
// backend validates against (universe_type.py ALLOWED_EXCHANGES / the SQL
// c.mic) and what categorical_exclusions.exchange_code carries. Transcribed
// verbatim from the Figma export's `majorExchanges` (label + mic + country);
// every MIC here is present in the backend EXCHANGE_TO_COUNTRY map, so an
// exclusion built from this list always validates.
export const STATIC_EXCHANGES: ReadonlyArray<{
  code: string;
  label: string;
  countryCode: string;
}> = [
  // United States
  { code: 'XNYS', label: 'New York Stock Exchange', countryCode: 'US' },
  { code: 'NYSE', label: 'New York Stock Exchange (Legacy)', countryCode: 'US' },
  { code: 'XNAS', label: 'Nasdaq', countryCode: 'US' },
  { code: 'XNGS', label: 'Nasdaq Global Select Market', countryCode: 'US' },
  { code: 'XNMS', label: 'Nasdaq Global Market', countryCode: 'US' },
  { code: 'XNCM', label: 'Nasdaq Capital Market', countryCode: 'US' },
  { code: 'OTCM', label: 'OTC Markets', countryCode: 'US' },
  { code: 'XOTC', label: 'OTC Link', countryCode: 'US' },
  { code: 'PINX', label: 'OTC Pink', countryCode: 'US' },
  { code: 'OOTC', label: 'Other OTC', countryCode: 'US' },
  { code: 'XASE', label: 'NYSE American', countryCode: 'US' },
  { code: 'AMEX', label: 'American Stock Exchange', countryCode: 'US' },
  { code: 'ARCX', label: 'NYSE Arca', countryCode: 'US' },
  { code: 'XPSX', label: 'Nasdaq PSX', countryCode: 'US' },
  { code: 'XCBO', label: 'Cboe Options Exchange', countryCode: 'US' },
  { code: 'BATS', label: 'Cboe BZX Exchange', countryCode: 'US' },
  { code: 'IEXG', label: 'Investors Exchange', countryCode: 'US' },
  // Canada
  { code: 'XTSE', label: 'Toronto Stock Exchange', countryCode: 'CA' },
  { code: 'XTSX', label: 'TSX Venture Exchange', countryCode: 'CA' },
  // Mexico
  { code: 'XMEX', label: 'Mexican Stock Exchange', countryCode: 'MX' },
  // Brazil
  { code: 'BVMF', label: 'B3 Brasil Bolsa Balcão', countryCode: 'BR' },
  // Chile
  { code: 'XSGO', label: 'Santiago Stock Exchange', countryCode: 'CL' },
  // Colombia
  { code: 'XBOG', label: 'Colombia Stock Exchange', countryCode: 'CO' },
  // Peru
  { code: 'XLIM', label: 'Lima Stock Exchange', countryCode: 'PE' },
  // United Kingdom
  { code: 'XLON', label: 'London Stock Exchange', countryCode: 'GB' },
  // Germany
  { code: 'XETR', label: 'Xetra', countryCode: 'DE' },
  { code: 'XFRA', label: 'Frankfurt Stock Exchange', countryCode: 'DE' },
  { code: 'XBER', label: 'Berlin Stock Exchange', countryCode: 'DE' },
  // France
  { code: 'XPAR', label: 'Euronext Paris', countryCode: 'FR' },
  { code: 'ALXP', label: 'Euronext Growth Paris', countryCode: 'FR' },
  // Netherlands
  { code: 'XAMS', label: 'Euronext Amsterdam', countryCode: 'NL' },
  // Belgium
  { code: 'XBRU', label: 'Euronext Brussels', countryCode: 'BE' },
  // Ireland
  { code: 'XDUB', label: 'Euronext Dublin', countryCode: 'IE' },
  // Portugal
  { code: 'XLIS', label: 'Euronext Lisbon', countryCode: 'PT' },
  // Switzerland
  { code: 'XSWX', label: 'SIX Swiss Exchange', countryCode: 'CH' },
  { code: 'XVTX', label: 'SIX Swiss Exchange (Blue Chips)', countryCode: 'CH' },
  // Italy
  { code: 'MTAA', label: 'Borsa Italiana MTA', countryCode: 'IT' },
  { code: 'XMIL', label: 'Borsa Italiana', countryCode: 'IT' },
  // Spain
  { code: 'BMEX', label: 'Bolsas y Mercados Españoles', countryCode: 'ES' },
  { code: 'XMAD', label: 'Bolsa de Madrid', countryCode: 'ES' },
  { code: 'XMCE', label: 'Spanish Market Segment', countryCode: 'ES' },
  // Sweden
  { code: 'XSTO', label: 'Nasdaq Stockholm', countryCode: 'SE' },
  { code: 'SSME', label: 'Swedish Market Segment', countryCode: 'SE' },
  // Finland
  { code: 'XHEL', label: 'Nasdaq Helsinki', countryCode: 'FI' },
  // Denmark
  { code: 'XCSE', label: 'Nasdaq Copenhagen', countryCode: 'DK' },
  // Norway
  { code: 'XOSL', label: 'Oslo Stock Exchange', countryCode: 'NO' },
  { code: 'MERK', label: 'Euronext Growth Oslo', countryCode: 'NO' },
  // Austria
  { code: 'XWBO', label: 'Vienna Stock Exchange', countryCode: 'AT' },
  { code: 'WBAH', label: 'Vienna Stock Exchange (Derivatives)', countryCode: 'AT' },
  // Greece
  { code: 'XATH', label: 'Athens Stock Exchange', countryCode: 'GR' },
  // Poland
  { code: 'XWAR', label: 'Warsaw Stock Exchange', countryCode: 'PL' },
  // Hungary
  { code: 'XBUD', label: 'Budapest Stock Exchange', countryCode: 'HU' },
  // Czech Republic
  { code: 'XPRA', label: 'Prague Stock Exchange', countryCode: 'CZ' },
  // Turkey
  { code: 'XIST', label: 'Borsa Istanbul', countryCode: 'TR' },
  { code: 'XEQY', label: 'Borsa Istanbul Equity Market', countryCode: 'TR' },
  // Russia
  { code: 'MISX', label: 'Moscow Exchange', countryCode: 'RU' },
  { code: 'RTSX', label: 'Russian Trading System', countryCode: 'RU' },
  // Japan
  { code: 'XTKS', label: 'Tokyo Stock Exchange', countryCode: 'JP' },
  { code: 'XNGO', label: 'Nagoya Stock Exchange', countryCode: 'JP' },
  { code: 'XFKA', label: 'Fukuoka Stock Exchange', countryCode: 'JP' },
  { code: 'XOSE', label: 'Osaka Exchange', countryCode: 'JP' },
  { code: 'XJAS', label: 'JASDAQ', countryCode: 'JP' },
  // Australia
  { code: 'XASX', label: 'Australian Securities Exchange', countryCode: 'AU' },
  // Hong Kong
  { code: 'XHKG', label: 'Hong Kong Stock Exchange', countryCode: 'HK' },
  // Singapore
  { code: 'XSES', label: 'Singapore Exchange', countryCode: 'SG' },
  // New Zealand
  { code: 'XNZE', label: 'New Zealand Exchange', countryCode: 'NZ' },
  // China
  { code: 'XSEC', label: 'China Securities Exchange', countryCode: 'CN' },
  { code: 'XSSC', label: 'Shanghai Stock Exchange', countryCode: 'CN' },
  { code: 'XSHE', label: 'Shenzhen Stock Exchange', countryCode: 'CN' },
  { code: 'XSHG', label: 'Shanghai Stock Exchange (Connect)', countryCode: 'CN' },
  // South Korea
  { code: 'XKRX', label: 'Korea Exchange', countryCode: 'KR' },
  { code: 'XKOS', label: 'KOSDAQ', countryCode: 'KR' },
  // Taiwan
  { code: 'XTAI', label: 'Taiwan Stock Exchange', countryCode: 'TW' },
  { code: 'ROCO', label: 'Taipei Exchange', countryCode: 'TW' },
  // Indonesia
  { code: 'XIDX', label: 'Indonesia Stock Exchange', countryCode: 'ID' },
  // Malaysia
  { code: 'XKLS', label: 'Bursa Malaysia', countryCode: 'MY' },
  // Thailand
  { code: 'XBKK', label: 'Stock Exchange of Thailand', countryCode: 'TH' },
  { code: 'XMAI', label: 'Market for Alternative Investment', countryCode: 'TH' },
  // Philippines
  { code: 'XPHS', label: 'Philippine Stock Exchange', countryCode: 'PH' },
  // India — XNSE was the one MIC index_constituents has ever carried that this
  // list lacked. Morningstar moved some Indian lines from XBOM to XNSE for
  // twelve days (2026-06-11 → 2026-06-23) then reverted, so it holds no rows in
  // the current snapshot: the API cannot surface it even when it is up, which
  // is precisely why the static list has to. Added to the backend's
  // EXCHANGE_TO_COUNTRY in the same change, or excluding it would fail
  // validate_exchanges().
  { code: 'XBOM', label: 'Bombay Stock Exchange', countryCode: 'IN' },
  { code: 'XNSE', label: 'National Stock Exchange of India', countryCode: 'IN' },
  // Pakistan
  { code: 'XKAR', label: 'Pakistan Stock Exchange', countryCode: 'PK' },
  // Israel
  { code: 'XTAE', label: 'Tel Aviv Stock Exchange', countryCode: 'IL' },
  // South Africa
  { code: 'XJSE', label: 'Johannesburg Stock Exchange', countryCode: 'ZA' },
  // Morocco
  { code: 'XCAS', label: 'Casablanca Stock Exchange', countryCode: 'MA' },
  // Egypt
  { code: 'XCAI', label: 'Egyptian Exchange', countryCode: 'EG' },
  // Qatar
  { code: 'DSMD', label: 'Qatar Stock Exchange', countryCode: 'QA' },
  // Kuwait
  { code: 'XKUW', label: 'Boursa Kuwait', countryCode: 'KW' },
  // Saudi Arabia
  { code: 'XSAU', label: 'Saudi Exchange', countryCode: 'SA' },
  // United Arab Emirates
  { code: 'XADS', label: 'Abu Dhabi Securities Exchange', countryCode: 'AE' },
  { code: 'XDFM', label: 'Dubai Financial Market', countryCode: 'AE' },
  { code: 'DIFX', label: 'Nasdaq Dubai', countryCode: 'AE' },
];


export const CLASSIFICATION_STATUS: Readonly<
  Record<string, 'current' | 'pending2026' | 'retired'>
> = {
  '10101': 'retired',
  '10102': 'retired',
  '10103': 'retired',
  '10104': 'retired',
  '10105': 'retired',
  '10106': 'retired',
  '10107': 'retired',
  '10110': 'current',
  '10120': 'current',
  '10130': 'current',
  '10140': 'current',
  '10150': 'current',
  '10160': 'current',
  '10200': 'current',
  '10208': 'retired',
  '10209': 'retired',
  '10210': 'retired',
  '10211': 'retired',
  '10212': 'retired',
  '10213': 'retired',
  '10214': 'retired',
  '10215': 'retired',
  '10216': 'retired',
  '10217': 'retired',
  '10218': 'retired',
  '10220': 'current',
  '10230': 'current',
  '10240': 'current',
  '10250': 'current',
  '10260': 'current',
  '10270': 'current',
  '10280': 'current',
  '10290': 'current',
  '10310': 'current',
  '10319': 'retired',
  '10320': 'current',
  '10321': 'retired',
  '10322': 'retired',
  '10323': 'retired',
  '10324': 'retired',
  '10325': 'retired',
  '10326': 'retired',
  '10330': 'current',
  '10340': 'current',
  '10350': 'current',
  '10360': 'current',
  '10370': 'pending2026',
  '10380': 'pending2026',
  '10410': 'current',
  '10420': 'current',
  '10427': 'retired',
  '10428': 'retired',
  '20510': 'current',
  '20520': 'current',
  '20525': 'current',
  '20529': 'retired',
  '20530': 'retired',
  '20531': 'retired',
  '20532': 'retired',
  '20533': 'retired',
  '20534': 'retired',
  '20540': 'current',
  '20550': 'current',
  '20560': 'current',
  '20570': 'pending2026',
  '20610': 'current',
  '20620': 'current',
  '20630': 'current',
  '20635': 'retired',
  '20636': 'retired',
  '20637': 'retired',
  '20638': 'retired',
  '20639': 'retired',
  '20640': 'retired',
  '20641': 'retired',
  '20642': 'retired',
  '20645': 'current',
  '20650': 'current',
  '20660': 'current',
  '20670': 'current',
  '20710': 'current',
  '20720': 'current',
  '20730': 'pending2026',
  '20743': 'retired',
  '20744': 'retired',
  '30810': 'current',
  '30820': 'current',
  '30830': 'current',
  '30845': 'retired',
  '30910': 'current',
  '30920': 'current',
  '30946': 'retired',
  '30947': 'retired',
  '30948': 'retired',
  '30949': 'retired',
  '30950': 'retired',
  '30951': 'retired',
  '31010': 'current',
  '31020': 'current',
  '31030': 'current',
  '31040': 'current',
  '31045': 'pending2026',
  '31050': 'current',
  '31052': 'retired',
  '31053': 'retired',
  '31054': 'retired',
  '31055': 'retired',
  '31056': 'retired',
  '31057': 'retired',
  '31058': 'retired',
  '31059': 'retired',
  '31060': 'current',
  '31061': 'retired',
  '31062': 'retired',
  '31063': 'retired',
  '31064': 'retired',
  '31070': 'current',
  '31080': 'current',
  '31090': 'current',
  '31110': 'current',
  '31120': 'current',
  '31130': 'current',
  '31165': 'retired',
  '31166': 'retired',
  '31167': 'retired',
  '31168': 'retired',
  '31169': 'retired',
  '10101001': 'retired',
  '10102002': 'retired',
  '10103003': 'retired',
  '10103004': 'retired',
  '10104005': 'retired',
  '10105006': 'retired',
  '10105007': 'retired',
  '10106008': 'retired',
  '10106009': 'retired',
  '10106010': 'retired',
  '10106011': 'retired',
  '10106012': 'retired',
  '10107013': 'retired',
  '10110010': 'current',
  '10120010': 'current',
  '10130010': 'current',
  '10130020': 'current',
  '10130030': 'pending2026',
  '10130040': 'pending2026',
  '10140010': 'current',
  '10140020': 'current',
  '10150010': 'current',
  '10150020': 'current',
  '10150030': 'current',
  '10150040': 'current',
  '10150050': 'current',
  '10150060': 'current',
  '10150070': 'pending2026',
  '10160010': 'current',
  '10160020': 'current',
  '10200010': 'current',
  '10200020': 'current',
  '10200030': 'current',
  '10200040': 'current',
  '10200050': 'pending2026',
  '10208014': 'retired',
  '10208015': 'retired',
  '10209016': 'retired',
  '10209017': 'retired',
  '10209018': 'retired',
  '10209019': 'retired',
  '10209020': 'retired',
  '10210021': 'retired',
  '10210022': 'retired',
  '10210023': 'retired',
  '10211024': 'retired',
  '10211025': 'retired',
  '10212026': 'retired',
  '10212027': 'retired',
  '10212028': 'retired',
  '10213029': 'retired',
  '10214030': 'retired',
  '10215031': 'retired',
  '10216032': 'retired',
  '10217033': 'retired',
  '10217034': 'retired',
  '10217035': 'retired',
  '10217036': 'retired',
  '10217037': 'retired',
  '10218038': 'retired',
  '10218039': 'retired',
  '10218040': 'retired',
  '10218041': 'retired',
  '10220010': 'current',
  '10230010': 'current',
  '10240010': 'current',
  '10240020': 'current',
  '10240030': 'current',
  '10240040': 'pending2026',
  '10250010': 'current',
  '10260010': 'current',
  '10270010': 'current',
  '10280010': 'current',
  '10280020': 'current',
  '10280030': 'current',
  '10280040': 'current',
  '10280050': 'current',
  '10280060': 'current',
  '10290010': 'current',
  '10290020': 'current',
  '10290030': 'current',
  '10290040': 'current',
  '10290050': 'current',
  '10290060': 'pending2026',
  '10310010': 'current',
  '10319042': 'retired',
  '10320010': 'current',
  '10320020': 'current',
  '10320030': 'current',
  '10320043': 'retired',
  '10320044': 'retired',
  '10320045': 'current',
  '10320046': 'retired',
  '10320047': 'retired',
  '10320048': 'retired',
  '10320049': 'retired',
  '10320050': 'retired',
  '10320051': 'retired',
  '10320052': 'retired',
  '10321053': 'retired',
  '10321054': 'retired',
  '10321055': 'retired',
  '10322056': 'retired',
  '10323057': 'retired',
  '10324058': 'retired',
  '10325059': 'retired',
  '10326060': 'retired',
  '10326061': 'retired',
  '10330010': 'current',
  '10330020': 'current',
  '10340010': 'current',
  '10340020': 'current',
  '10340030': 'current',
  '10340040': 'current',
  '10340050': 'current',
  '10340060': 'current',
  '10350010': 'current',
  '10350020': 'current',
  '10350030': 'pending2026',
  '10360010': 'current',
  '10370010': 'pending2026',
  '10380010': 'pending2026',
  '10410010': 'current',
  '10410020': 'current',
  '10410030': 'current',
  '10410040': 'pending2026',
  '10420010': 'current',
  '10420020': 'current',
  '10420030': 'current',
  '10420040': 'current',
  '10420050': 'current',
  '10420060': 'current',
  '10420070': 'current',
  '10420080': 'current',
  '10420090': 'current',
  '10420100': 'pending2026',
  '10427062': 'retired',
  '10427063': 'retired',
  '10428064': 'retired',
  '10428065': 'retired',
  '10428066': 'retired',
  '10428067': 'retired',
  '10428068': 'retired',
  '10428069': 'retired',
  '10428070': 'retired',
  '20510010': 'current',
  '20510020': 'current',
  '20510030': 'pending2026',
  '20520010': 'current',
  '20525010': 'current',
  '20525020': 'current',
  '20525030': 'current',
  '20525040': 'current',
  '20529071': 'retired',
  '20529072': 'retired',
  '20530073': 'retired',
  '20531074': 'retired',
  '20531075': 'retired',
  '20531076': 'retired',
  '20531077': 'retired',
  '20532078': 'retired',
  '20533079': 'retired',
  '20533080': 'retired',
  '20533081': 'retired',
  '20533082': 'retired',
  '20534083': 'retired',
  '20540010': 'current',
  '20550010': 'current',
  '20550020': 'current',
  '20550030': 'current',
  '20550040': 'pending2026',
  '20560010': 'current',
  '20570010': 'pending2026',
  '20610010': 'current',
  '20620010': 'current',
  '20620020': 'current',
  '20630010': 'current',
  '20635084': 'retired',
  '20636085': 'retired',
  '20636086': 'retired',
  '20637087': 'retired',
  '20638088': 'retired',
  '20638089': 'retired',
  '20639090': 'retired',
  '20640091': 'retired',
  '20641092': 'retired',
  '20642093': 'retired',
  '20645010': 'current',
  '20645020': 'current',
  '20645030': 'current',
  '20650010': 'current',
  '20650020': 'current',
  '20650030': 'pending2026',
  '20660010': 'current',
  '20670010': 'current',
  '20710010': 'current',
  '20710020': 'current',
  '20720010': 'current',
  '20720020': 'current',
  '20720030': 'current',
  '20720040': 'current',
  '20730010': 'pending2026',
  '20743094': 'retired',
  '20744095': 'retired',
  '20744096': 'retired',
  '20744097': 'retired',
  '20744098': 'retired',
  '30810010': 'current',
  '30820010': 'current',
  '30820020': 'current',
  '30820030': 'current',
  '30820040': 'current',
  '30830010': 'current',
  '30830020': 'current',
  '30845099': 'retired',
  '30845100': 'retired',
  '30910010': 'current',
  '30910020': 'current',
  '30910030': 'current',
  '30910040': 'current',
  '30910050': 'current',
  '30910060': 'current',
  '30910070': 'pending2026',
  '30920010': 'current',
  '30920020': 'current',
  '30920030': 'pending2026',
  '30946101': 'retired',
  '30947102': 'retired',
  '30948103': 'retired',
  '30949104': 'retired',
  '30950105': 'retired',
  '30951106': 'retired',
  '31010010': 'current',
  '31020010': 'current',
  '31020020': 'current',
  '31020030': 'current',
  '31020040': 'current',
  '31020050': 'current',
  '31030010': 'current',
  '31040010': 'current',
  '31040020': 'current',
  '31040030': 'current',
  '31045010': 'pending2026',
  '31050010': 'current',
  '31052107': 'retired',
  '31053108': 'retired',
  '31054109': 'retired',
  '31055110': 'retired',
  '31056111': 'retired',
  '31056112': 'retired',
  '31057113': 'retired',
  '31058114': 'retired',
  '31058115': 'retired',
  '31059116': 'retired',
  '31060010': 'current',
  '31060117': 'retired',
  '31061118': 'retired',
  '31061119': 'retired',
  '31061120': 'retired',
  '31061121': 'retired',
  '31061122': 'retired',
  '31062123': 'retired',
  '31062124': 'retired',
  '31062125': 'retired',
  '31062126': 'retired',
  '31062127': 'retired',
  '31063128': 'retired',
  '31064129': 'retired',
  '31070010': 'current',
  '31070020': 'current',
  '31070030': 'current',
  '31070040': 'current',
  '31070050': 'current',
  '31070060': 'current',
  '31070070': 'pending2026',
  '31080010': 'current',
  '31080020': 'current',
  '31080030': 'current',
  '31080040': 'current',
  '31080050': 'current',
  '31080060': 'current',
  '31080070': 'pending2026',
  '31080080': 'pending2026',
  '31090010': 'current',
  '31110010': 'current',
  '31110020': 'current',
  '31110030': 'current',
  '31110040': 'pending2026',
  '31120010': 'current',
  '31120020': 'current',
  '31120030': 'current',
  '31120040': 'current',
  '31120050': 'current',
  '31120060': 'current',
  '31130010': 'current',
  '31130020': 'current',
  '31130030': 'current',
  '31165130': 'retired',
  '31165131': 'retired',
  '31165132': 'retired',
  '31165133': 'retired',
  '31165134': 'retired',
  '31166135': 'retired',
  '31167136': 'retired',
  '31167137': 'retired',
  '31167138': 'retired',
  '31167139': 'retired',
  '31167140': 'retired',
  '31167141': 'retired',
  '31167142': 'retired',
  '31167143': 'retired',
  '31168144': 'retired',
  '31169145': 'retired',
  '31169146': 'retired',
  '31169147': 'retired',
  '31169148': 'retired',
};

/**
 * Which published Morningstar document each label was taken from, so anyone
 * reading a code here has a path back to the source. Mirrors LABEL_SOURCE in
 * lib/py/biasafe/.../static_data/classification.py, the single source of truth.
 *
 *   2011  Global Equity Classification Structure, 2011 -- 148 industries /
 *         69 groups, 8-digit codes ending in a global 001-148 counter.
 *         Retired from our feed over 2019-2021.
 *   2019  Global Equity Classification Structure, 2019 -- 145 / 55.
 *         What ClickHouse still carries today.
 *   2026  Global ENTITY Classification Structure, Feb 2026 -- 156 / 59.
 *         Effective 2026-07-13 but NOT yet in our feed.
 *
 * Where a code appears in several documents the MOST RECENT name wins, so this
 * is the document the label came from -- not the only one the code appears in.
 * The 11 sectors are identical in all three and carry no vintage.
 */
export const LABEL_SOURCE: Readonly<Record<string, '2011' | '2019' | '2026'>> = {
  '10101': '2011',
  '10102': '2011',
  '10103': '2011',
  '10104': '2011',
  '10105': '2011',
  '10106': '2011',
  '10107': '2011',
  '10110': '2026',
  '10120': '2026',
  '10130': '2026',
  '10140': '2026',
  '10150': '2026',
  '10160': '2026',
  '10200': '2026',
  '10208': '2011',
  '10209': '2011',
  '10210': '2011',
  '10211': '2011',
  '10212': '2011',
  '10213': '2011',
  '10214': '2011',
  '10215': '2011',
  '10216': '2011',
  '10217': '2011',
  '10218': '2011',
  '10220': '2026',
  '10230': '2026',
  '10240': '2026',
  '10250': '2026',
  '10260': '2026',
  '10270': '2026',
  '10280': '2026',
  '10290': '2026',
  '10310': '2026',
  '10319': '2011',
  '10320': '2026',
  '10321': '2011',
  '10322': '2011',
  '10323': '2011',
  '10324': '2011',
  '10325': '2011',
  '10326': '2011',
  '10330': '2026',
  '10340': '2026',
  '10350': '2026',
  '10360': '2026',
  '10370': '2026',
  '10380': '2026',
  '10410': '2026',
  '10420': '2026',
  '10427': '2011',
  '10428': '2011',
  '20510': '2026',
  '20520': '2019',
  '20525': '2026',
  '20529': '2011',
  '20530': '2011',
  '20531': '2011',
  '20532': '2011',
  '20533': '2011',
  '20534': '2011',
  '20540': '2026',
  '20550': '2026',
  '20560': '2026',
  '20570': '2026',
  '20610': '2026',
  '20620': '2026',
  '20630': '2026',
  '20635': '2011',
  '20636': '2011',
  '20637': '2011',
  '20638': '2011',
  '20639': '2011',
  '20640': '2011',
  '20641': '2011',
  '20642': '2011',
  '20645': '2026',
  '20650': '2026',
  '20660': '2026',
  '20670': '2026',
  '20710': '2026',
  '20720': '2026',
  '20730': '2026',
  '20743': '2011',
  '20744': '2011',
  '30810': '2026',
  '30820': '2026',
  '30830': '2026',
  '30845': '2011',
  '30910': '2026',
  '30920': '2026',
  '30946': '2011',
  '30947': '2011',
  '30948': '2011',
  '30949': '2011',
  '30950': '2011',
  '30951': '2011',
  '31010': '2026',
  '31020': '2026',
  '31030': '2026',
  '31040': '2026',
  '31045': '2026',
  '31050': '2026',
  '31052': '2011',
  '31053': '2011',
  '31054': '2011',
  '31055': '2011',
  '31056': '2011',
  '31057': '2011',
  '31058': '2011',
  '31059': '2011',
  '31060': '2026',
  '31061': '2011',
  '31062': '2011',
  '31063': '2011',
  '31064': '2011',
  '31070': '2026',
  '31080': '2026',
  '31090': '2026',
  '31110': '2026',
  '31120': '2026',
  '31130': '2026',
  '31165': '2011',
  '31166': '2011',
  '31167': '2011',
  '31168': '2011',
  '31169': '2011',
  '10101001': '2011',
  '10102002': '2011',
  '10103003': '2011',
  '10103004': '2011',
  '10104005': '2011',
  '10105006': '2011',
  '10105007': '2011',
  '10106008': '2011',
  '10106009': '2011',
  '10106010': '2011',
  '10106011': '2011',
  '10106012': '2011',
  '10107013': '2011',
  '10110010': '2026',
  '10120010': '2026',
  '10130010': '2026',
  '10130020': '2026',
  '10130030': '2026',
  '10130040': '2026',
  '10140010': '2026',
  '10140020': '2026',
  '10150010': '2026',
  '10150020': '2026',
  '10150030': '2026',
  '10150040': '2026',
  '10150050': '2026',
  '10150060': '2026',
  '10150070': '2026',
  '10160010': '2026',
  '10160020': '2026',
  '10200010': '2026',
  '10200020': '2026',
  '10200030': '2026',
  '10200040': '2026',
  '10200050': '2026',
  '10208014': '2011',
  '10208015': '2011',
  '10209016': '2011',
  '10209017': '2011',
  '10209018': '2011',
  '10209019': '2011',
  '10209020': '2011',
  '10210021': '2011',
  '10210022': '2011',
  '10210023': '2011',
  '10211024': '2011',
  '10211025': '2011',
  '10212026': '2011',
  '10212027': '2011',
  '10212028': '2011',
  '10213029': '2011',
  '10214030': '2011',
  '10215031': '2011',
  '10216032': '2011',
  '10217033': '2011',
  '10217034': '2011',
  '10217035': '2011',
  '10217036': '2011',
  '10217037': '2011',
  '10218038': '2011',
  '10218039': '2011',
  '10218040': '2011',
  '10218041': '2011',
  '10220010': '2026',
  '10230010': '2026',
  '10240010': '2026',
  '10240020': '2026',
  '10240030': '2026',
  '10240040': '2026',
  '10250010': '2026',
  '10260010': '2026',
  '10270010': '2026',
  '10280010': '2026',
  '10280020': '2026',
  '10280030': '2026',
  '10280040': '2019',
  '10280050': '2026',
  '10280060': '2026',
  '10290010': '2019',
  '10290020': '2026',
  '10290030': '2026',
  '10290040': '2026',
  '10290050': '2026',
  '10290060': '2026',
  '10310010': '2026',
  '10319042': '2011',
  '10320010': '2026',
  '10320020': '2026',
  '10320030': '2026',
  '10320043': '2011',
  '10320044': '2011',
  '10320045': '2011',
  '10320046': '2011',
  '10320047': '2011',
  '10320048': '2011',
  '10320049': '2011',
  '10320050': '2011',
  '10320051': '2011',
  '10320052': '2011',
  '10321053': '2011',
  '10321054': '2011',
  '10321055': '2011',
  '10322056': '2011',
  '10323057': '2011',
  '10324058': '2011',
  '10325059': '2011',
  '10326060': '2011',
  '10326061': '2011',
  '10330010': '2026',
  '10330020': '2026',
  '10340010': '2026',
  '10340020': '2026',
  '10340030': '2026',
  '10340040': '2019',
  '10340050': '2026',
  '10340060': '2026',
  '10350010': '2026',
  '10350020': '2026',
  '10350030': '2026',
  '10360010': '2026',
  '10370010': '2026',
  '10380010': '2026',
  '10410010': '2026',
  '10410020': '2026',
  '10410030': '2026',
  '10410040': '2026',
  '10420010': '2026',
  '10420020': '2026',
  '10420030': '2026',
  '10420040': '2026',
  '10420050': '2026',
  '10420060': '2026',
  '10420070': '2019',
  '10420080': '2026',
  '10420090': '2026',
  '10420100': '2026',
  '10427062': '2011',
  '10427063': '2011',
  '10428064': '2011',
  '10428065': '2011',
  '10428066': '2011',
  '10428067': '2011',
  '10428068': '2011',
  '10428069': '2011',
  '10428070': '2011',
  '20510010': '2026',
  '20510020': '2026',
  '20510030': '2026',
  '20520010': '2019',
  '20525010': '2026',
  '20525020': '2026',
  '20525030': '2026',
  '20525040': '2026',
  '20529071': '2011',
  '20529072': '2011',
  '20530073': '2011',
  '20531074': '2011',
  '20531075': '2011',
  '20531076': '2011',
  '20531077': '2011',
  '20532078': '2011',
  '20533079': '2011',
  '20533080': '2011',
  '20533081': '2011',
  '20533082': '2011',
  '20534083': '2011',
  '20540010': '2026',
  '20550010': '2026',
  '20550020': '2026',
  '20550030': '2026',
  '20550040': '2026',
  '20560010': '2026',
  '20570010': '2026',
  '20610010': '2026',
  '20620010': '2026',
  '20620020': '2019',
  '20630010': '2026',
  '20635084': '2011',
  '20636085': '2011',
  '20636086': '2011',
  '20637087': '2011',
  '20638088': '2011',
  '20638089': '2011',
  '20639090': '2011',
  '20640091': '2011',
  '20641092': '2011',
  '20642093': '2011',
  '20645010': '2026',
  '20645020': '2019',
  '20645030': '2026',
  '20650010': '2026',
  '20650020': '2026',
  '20650030': '2026',
  '20660010': '2026',
  '20670010': '2026',
  '20710010': '2026',
  '20710020': '2026',
  '20720010': '2026',
  '20720020': '2026',
  '20720030': '2026',
  '20720040': '2019',
  '20730010': '2026',
  '20743094': '2011',
  '20744095': '2011',
  '20744096': '2011',
  '20744097': '2011',
  '20744098': '2011',
  '30810010': '2026',
  '30820010': '2026',
  '30820020': '2026',
  '30820030': '2026',
  '30820040': '2026',
  '30830010': '2026',
  '30830020': '2026',
  '30845099': '2011',
  '30845100': '2011',
  '30910010': '2019',
  '30910020': '2026',
  '30910030': '2026',
  '30910040': '2026',
  '30910050': '2026',
  '30910060': '2026',
  '30910070': '2026',
  '30920010': '2026',
  '30920020': '2026',
  '30920030': '2026',
  '30946101': '2011',
  '30947102': '2011',
  '30948103': '2011',
  '30949104': '2011',
  '30950105': '2011',
  '30951106': '2011',
  '31010010': '2026',
  '31020010': '2026',
  '31020020': '2026',
  '31020030': '2026',
  '31020040': '2026',
  '31020050': '2026',
  '31030010': '2026',
  '31040010': '2026',
  '31040020': '2019',
  '31040030': '2019',
  '31045010': '2026',
  '31050010': '2026',
  '31052107': '2011',
  '31053108': '2011',
  '31054109': '2011',
  '31055110': '2011',
  '31056111': '2011',
  '31056112': '2011',
  '31057113': '2011',
  '31058114': '2011',
  '31058115': '2011',
  '31059116': '2011',
  '31060010': '2026',
  '31060117': '2011',
  '31061118': '2011',
  '31061119': '2011',
  '31061120': '2011',
  '31061121': '2011',
  '31061122': '2011',
  '31062123': '2011',
  '31062124': '2011',
  '31062125': '2011',
  '31062126': '2011',
  '31062127': '2011',
  '31063128': '2011',
  '31064129': '2011',
  '31070010': '2026',
  '31070020': '2026',
  '31070030': '2026',
  '31070040': '2026',
  '31070050': '2026',
  '31070060': '2026',
  '31070070': '2026',
  '31080010': '2026',
  '31080020': '2026',
  '31080030': '2026',
  '31080040': '2026',
  '31080050': '2026',
  '31080060': '2026',
  '31080070': '2026',
  '31080080': '2026',
  '31090010': '2026',
  '31110010': '2026',
  '31110020': '2026',
  '31110030': '2026',
  '31110040': '2026',
  '31120010': '2026',
  '31120020': '2026',
  '31120030': '2026',
  '31120040': '2026',
  '31120050': '2026',
  '31120060': '2026',
  '31130010': '2026',
  '31130020': '2026',
  '31130030': '2019',
  '31165130': '2011',
  '31165131': '2011',
  '31165132': '2011',
  '31165133': '2011',
  '31165134': '2011',
  '31166135': '2011',
  '31167136': '2011',
  '31167137': '2011',
  '31167138': '2011',
  '31167139': '2011',
  '31167140': '2011',
  '31167141': '2011',
  '31167142': '2011',
  '31167143': '2011',
  '31168144': '2011',
  '31169145': '2011',
  '31169146': '2011',
  '31169147': '2011',
  '31169148': '2011',
};
